import { useNavigate } from 'react-router-dom';

import {
  AssetVersionSwitch,
  type TAssetDocumentView,
} from '@/layouts/AssetWorkspaceLayout';
import { getRoutePath } from '@/routes/util';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';

/** Which of an automation's versions the workspace is showing. */
export type TAutomationVersionView = TAssetDocumentView;

/**
 * Props for {@link AutomationVersionSelect}.
 */
export interface IAutomationVersionSelectProps {
  automationId: string;
  view: TAutomationVersionView;
  /** The published version number, without the `v`. */
  liveVersion?: string;
  /** The draft's version, shown only while it is ahead of the live one. */
  draftVersion?: string;
  /** Dims the Live badge for a turned-off automation. */
  isDeactivated?: boolean;
}

/**
 * An automation's {@link AssetVersionSwitch}: the live version is the
 * published document and the draft is the editor, so switching navigates.
 * It replaces the history entry, so Back leaves the automation instead of
 * stepping back through the versions.
 */
export function AutomationVersionSelect({
  automationId,
  view,
  liveVersion,
  draftVersion,
  isDeactivated,
}: IAutomationVersionSelectProps) {
  const navigate = useNavigate();
  const orgId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  return (
    <AssetVersionSwitch
      value={view}
      onValueChange={next =>
        navigate(
          next === 'live'
            ? getRoutePath('AUTOMATION_AOP', {
                orgId,
                workspaceId,
                automationId,
              })
            : getRoutePath('DRAFT_EDITOR', {
                orgId,
                workspaceId,
                draftId: automationId,
              }),
          { replace: true },
        )
      }
      liveVersion={liveVersion}
      draftVersion={draftVersion}
      isLiveDeactivated={isDeactivated}
      data-testid="automation-version-select"
    />
  );
}
